import React, { createContext, useCallback, useReducer } from 'react';

/**
 * NotificacionesContext
 *
 * Contexto global para gestionar el estado de las notificaciones
 * Permite actualizar y sincronizar notificaciones desde cualquier componente
 */
export const NotificacionesContext = createContext();

/**
 * Action types para el reducer
 */
const ACTIONS = {
  SET_NOTIFICACIONES: 'SET_NOTIFICACIONES',
  REMOVE_NOTIFICACION: 'REMOVE_NOTIFICACION',
  CLEAR_NOTIFICACIONES: 'CLEAR_NOTIFICACIONES',
  UPDATE_CONTADOR: 'UPDATE_CONTADOR',
};

/**
 * Estado inicial del contexto
 */
const initialState = {
  notificaciones: [],
  contadorNotificaciones: 0,
};

/**
 * Reducer para gestionar las acciones sobre el estado de notificaciones
 *
 * @param {object} state - Estado actual
 * @param {object} action - Acción a ejecutar con su payload
 * @returns {object} Nuevo estado
 */
const notificacionesReducer = (state, action) => {
  switch (action.type) {
    // Carga todas las notificaciones y calcula el contador
    case ACTIONS.SET_NOTIFICACIONES:
      return {
        ...state,
        notificaciones: action.payload,
        contadorNotificaciones: action.payload?.length || 0,
      };

    // Elimina una notificación específica y decrementa el contador
    case ACTIONS.REMOVE_NOTIFICACION:
      return {
        ...state,
        notificaciones: state.notificaciones.filter((n) => n.numeroNotificacion !== action.payload),
        contadorNotificaciones: Math.max(0, state.contadorNotificaciones - 1),
      };

    // Limpia todas las notificaciones
    case ACTIONS.CLEAR_NOTIFICACIONES:
      return {
        ...state,
        notificaciones: [],
        contadorNotificaciones: 0,
      };

    // Actualiza manualmente el contador
    case ACTIONS.UPDATE_CONTADOR:
      return {
        ...state,
        contadorNotificaciones: action.payload,
      };

    default:
      return state;
  }
};

/**
 * NotificacionesProvider - Proveedor del contexto
 *
 * Envuelve la aplicación y proporciona el contexto de notificaciones
 * a todos los componentes hijos
 *
 * @param {ReactNode} children - Componentes hijos a envolver
 * @returns {JSX.Element} Provider con contexto configurado
 */
export const NotificacionesProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificacionesReducer, initialState);

  /**
   * Carga las notificaciones en el contexto
   *
   * @param {array} notificaciones - Array de notificaciones a cargar
   */
  const setNotificaciones = useCallback((notificaciones) => {
    dispatch({
      type: ACTIONS.SET_NOTIFICACIONES,
      payload: notificaciones,
    });
  }, []);

  /**
   * Elimina una notificación y actualiza el contador
   *
   * @param {string} numeroNotificacion - ID de la notificación a eliminar
   */
  const removerNotificacion = useCallback((numeroNotificacion) => {
    dispatch({
      type: ACTIONS.REMOVE_NOTIFICACION,
      payload: numeroNotificacion,
    });
  }, []);

  /**
   * Limpia todas las notificaciones
   */
  const limpiarNotificaciones = useCallback(() => {
    dispatch({
      type: ACTIONS.CLEAR_NOTIFICACIONES,
    });
  }, []);

  /**
   * Actualiza manualmente el contador de notificaciones
   *
   * @param {number} cantidad - Nueva cantidad de notificaciones
   */
  const actualizarContador = useCallback((cantidad) => {
    dispatch({
      type: ACTIONS.UPDATE_CONTADOR,
      payload: cantidad,
    });
  }, []);

  const value = {
    // Estado
    notificaciones: state.notificaciones,
    contadorNotificaciones: state.contadorNotificaciones,
    // Acciones
    setNotificaciones,
    removerNotificacion,
    limpiarNotificaciones,
    actualizarContador,
  };

  return <NotificacionesContext.Provider value={value}>{children}</NotificacionesContext.Provider>;
};
